import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { haptic, shareToTelegram, shareToX } from "./telegram";
import { pfpUrl } from "./pfp";
import type {
  BoardTab,
  Booga,
  BoogaDetail,
  ChallengeCreated,
  ChallengePreview,
  DirectoryEntry,
  DirectoryStatus,
  FightResponse,
  FighterSummary,
  LeaderboardEntry,
  LobbyChallenge,
  PvpFightResponse,
  PvpLeaderboardEntry,
  QueueJoinResult,
  QueueStatusResult,
  RenderedLine,
  TournamentResponse,
} from "./types";
import {
  ArchetypePill,
  archetypeColor,
  BoogaArt,
  BoogaCard,
  BottomSheet,
  Button,
  HpBar,
  Plaque,
  RarityBadge,
  rarityTier,
  Spinner,
  StatBars,
  type RarityTier,
} from "./ui";

/** Telegram paper-plane glyph, corner badge on the share button. */
function TelegramIcon() {
  return (
    <svg className="btn-badge" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M21.94 4.3 18.7 19.6c-.24 1.08-.88 1.34-1.78.84l-4.92-3.63-2.37 2.28c-.26.26-.48.48-.99.48l.35-5 9.1-8.22c.4-.35-.09-.55-.62-.2L4.22 12.4l-4.84-1.52c-1.05-.33-1.07-1.05.22-1.55L20.6 2.9c.88-.32 1.65.2 1.34 1.4Z"
        transform="translate(1 0)"
      />
    </svg>
  );
}

/** X (Twitter) glyph, corner badge on the share button. */
function XIcon() {
  return (
    <svg className="btn-badge" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M18.9 1.5h3.4l-7.42 8.48L24 22.5h-6.84l-5.36-7-6.13 7H2.27l7.94-9.08L0 1.5h7.01l4.85 6.41L18.9 1.5Zm-1.2 18.93h1.88L6.38 3.42H4.36L17.7 20.43Z"
      />
    </svg>
  );
}

/** Winner's close-up portrait on the result screen. Prefers the dedicated PFP set,
 * falls back to the in-fight media, and hides itself if neither resolves. */
function WinnerPfp({ fighter }: { fighter: FighterSummary }) {
  const primary = pfpUrl(fighter.identifier, fighter.name);
  const [src, setSrc] = useState(primary ?? fighter.mediaUrl);
  if (!src) return null;
  return (
    <div className="winner-pfp">
      <img
        src={src}
        alt={fighter.name}
        loading="eager"
        onError={() => {
          // PFP missing → drop to the fighter's existing media; then give up.
          if (src !== fighter.mediaUrl && fighter.mediaUrl) setSrc(fighter.mediaUrl);
          else setSrc(undefined);
        }}
      />
    </div>
  );
}

/** Result-screen share controls: forward to a Telegram chat or compose a tweet.
 *  `link` overrides the default share URL (PvP challenge deep links). */
function ShareBar({ text, link }: { text: string; link?: string }) {
  return (
    <div className="share-bar">
      <Button variant="cyber" full onClick={() => shareToTelegram(text, link)}>
        Share to Telegram
        <TelegramIcon />
      </Button>
      <Button variant="cyber" full onClick={() => shareToX(link ? `${text} ${link}` : text)}>
        Share to X
        <XIcon />
      </Button>
    </div>
  );
}

/**
 * Winner-focused brag for a single fight. Stalemate-aware so we never claim a
 * "win" on a draw. Handles/hashtag and the bot link are appended downstream by
 * shareToX / shareToTelegram, so keep this to the result sentence.
 */
function fightShareText(winner: FighterSummary, rounds: number, draw: boolean): string {
  return draw
    ? `🦴 My ${winner.name} (${winner.archetype}) survived a Booga Brawl to a draw after ${rounds} rounds! Ooga Booga.`
    : `🦴 My ${winner.name} (${winner.archetype}) won a Booga Brawl in ${rounds} rounds! Ooga Booga.`;
}

/** Winner-focused brag for a tournament champion. */
function tournamentShareText(champName: string, fights: number): string {
  return `🏆 My ${champName} survived the Booga Trials and took the crown across ${fights} fights! Ooga Booga.`;
}

function TopBar({
  title,
  onBack,
  right,
}: {
  title: React.ReactNode;
  onBack?: () => void;
  right?: React.ReactNode;
}) {
  return (
    <header className="topbar">
      {onBack ? (
        <button className="icon-back" onClick={onBack} aria-label="Back">
          <img src="/brand/Aroow_Left.png" alt="Back" />
        </button>
      ) : (
        <span />
      )}
      <Plaque>{title}</Plaque>
      <span className="topbar-right">{right}</span>
    </header>
  );
}

// --- Cave (home) ------------------------------------------------------------

export function CaveScreen({
  onConnect,
  onLeaderboard,
  loading,
  error,
  telegram,
}: {
  onConnect: () => void;
  onLeaderboard: () => void;
  loading: boolean;
  error?: string;
  /** Telegram identity status for this browser (bot doorway). */
  telegram: { linked: boolean; username: string | null; botUrl: string; onUnlink: () => void };
}) {
  return (
    <div className="screen cave">
      <div className="hero">
        <div className="runes" aria-hidden>
          ✦ ◉ ⟁ ☼ ✦
        </div>
        <img className="logo-img" src="/brand/logo.png" alt="Booga Brawl" />
        <div className="tagline">
          <span className="tagline-lead">Send your Boogas into battle!</span>
          <span>Combat power is based on the</span>
          <span>rarity of the traits.</span>
          <span>The rarer, the harder they hit.</span>
        </div>
      </div>

      <div className="card paste-card">
        {telegram.linked ? (
          <p className="tg-status linked">
            Telegram: <b>{telegram.username ? `@${telegram.username}` : "linked"}</b>{" "}
            <button className="link small" onClick={telegram.onUnlink}>
              not you?
            </button>
          </p>
        ) : (
          <div className="tg-status">
            <p className="hint">
              Step 1 — get your personal link from the bot (send it <b>/play</b>), then open
              that link here or in xPortal's Browser.
            </p>
            <a className="btn btn-ghost btn-full" href={telegram.botUrl} target="_blank" rel="noreferrer">
              Open the Booga Brawl bot ✈️
            </a>
          </div>
        )}
        <p className="hint">
          {telegram.linked ? "Step 2 — c" : "C"}onnect your MultiversX wallet and sign to prove
          it's yours — then your Boogas can brawl.
        </p>
        {error ? <p className="error">{error}</p> : null}
        <Button variant="lava" full onClick={onConnect} disabled={loading}>
          {loading ? "Summoning Boogas…" : "Connect Wallet 🦴"}
        </Button>
      </div>

      <button className="link big" onClick={onLeaderboard}>
        <img className="lb-trophy" src="/brand/Rank.png" alt="" aria-hidden />
        Leaderboard
      </button>
      <p className="footnote">We never store your wallet — you sign, we verify. Ooga Booga.</p>
    </div>
  );
}

// --- Roster -----------------------------------------------------------------

export function RosterScreen({
  roster,
  modes,
  busy,
  onOneVsOne,
  onPool,
  onTournament,
  onChallenge,
  onQueue,
  onLobby,
  onLeaderboard,
  onBack,
}: {
  roster: Booga[];
  modes: { poolFight: boolean; oneVsOne: boolean; tournament: boolean };
  busy: boolean;
  onOneVsOne: (a: string, b: string) => void;
  onPool: (id: string) => void;
  onTournament: (ids: string[]) => void;
  onChallenge: (id: string) => void;
  onQueue: (id: string) => void;
  onLobby: () => void;
  onLeaderboard: () => void;
  onBack: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [view, setView] = useState<RosterView>(getRosterView);
  const [pvpSheet, setPvpSheet] = useState(false);

  // Filter/sort state. Sort persists; text query + chip filters are session-local.
  const [query, setQuery] = useState("");
  const [archetypes, setArchetypes] = useState<Set<string>>(() => new Set());
  const [tiers, setTiers] = useState<Set<RarityTier>>(() => new Set());
  const [sort, setSort] = useState<RosterSort>(getRosterSort);
  // Which bottom sheet is open (if any), and whether the inline search is shown.
  const [sheet, setSheet] = useState<null | "filter" | "sort">(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Only offer filter chips for values actually present in this roster.
  const presentArchetypes = useMemo(() => {
    const s = new Set<string>();
    for (const b of roster) s.add(b.archetype);
    return [...s].sort();
  }, [roster]);
  const presentTiers = useMemo(() => {
    const s = new Set<RarityTier>();
    for (const b of roster) s.add(rarityTier(b.rank));
    return TIER_ORDER.filter((t) => s.has(t));
  }, [roster]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roster
      .filter((b) => {
        if (archetypes.size && !archetypes.has(b.archetype)) return false;
        if (tiers.size && !tiers.has(rarityTier(b.rank))) return false;
        if (q && !b.name.toLowerCase().includes(q) && !b.identifier.toLowerCase().includes(q))
          return false;
        return true;
      })
      .sort((a, b) => compareBoogas(a, b, sort));
  }, [roster, query, archetypes, tiers, sort]);

  const hiddenSelected = selected.filter(
    (id) => !shown.some((b) => b.identifier === id),
  ).length;
  const activeFilterCount = archetypes.size + tiers.size;
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label ?? "Rarest";

  function clearFilters() {
    haptic("light");
    setQuery("");
    setArchetypes(new Set());
    setTiers(new Set());
  }

  function changeView(v: RosterView) {
    haptic("light");
    setView(v);
    setRosterView(v);
  }

  function changeSort(s: RosterSort) {
    haptic("light");
    setSort(s);
    setRosterSort(s);
  }

  function toggleArchetype(a: string) {
    haptic("light");
    setArchetypes((prev) => {
      const next = new Set(prev);
      next.has(a) ? next.delete(a) : next.add(a);
      return next;
    });
  }

  function toggleTier(t: RarityTier) {
    haptic("light");
    setTiers((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  }

  function toggle(id: string) {
    haptic("light");
    setSelected((s) =>
      s.includes(id)
        ? s.filter((x) => x !== id)
        : s.length < 2
          ? [...s, id]
          : [s[1]!, id],
    );
  }

  const canOne = selected.length === 2;
  const canPool = selected.length === 1;

  return (
    <div className="screen">
      <TopBar
        title="Your Boogas"
        onBack={onBack}
        right={
          <button className="link" onClick={onLeaderboard} aria-label="Leaderboard">
            <img className="lb-trophy" src="/brand/Rank.png" alt="" aria-hidden />
          </button>
        }
      />
      <p className="hint">
        Tap a Booga to choose it:
        <br />
        1 Booga for a Pool Brawl
        <br />
        2 Boogas for a 1v1
        {modes.tournament ? (
          <>
            <br />
            Booga Trials = battle with your whole squad.
          </>
        ) : null}
      </p>

      {roster.length > 1 && (
        <div className="roster-toolbar">
          <button
            type="button"
            className={searchOpen || query ? "toolbar-btn active" : "toolbar-btn"}
            aria-pressed={searchOpen || !!query}
            aria-label="Search"
            onClick={() => {
              haptic("light");
              setSearchOpen((o) => !o);
            }}
          >
            🔍
          </button>
          <button
            type="button"
            className={activeFilterCount > 0 ? "toolbar-btn active" : "toolbar-btn"}
            onClick={() => {
              haptic("light");
              setSheet("filter");
            }}
          >
            ⚙ Filters
            {activeFilterCount > 0 && <span className="toolbar-badge">{activeFilterCount}</span>}
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => {
              haptic("light");
              setSheet("sort");
            }}
          >
            ⇅ {sortLabel}
          </button>
          <span className="filter-count">
            {shown.length}/{roster.length}
          </span>
        </div>
      )}

      {roster.length > 1 && searchOpen && (
        <div className="filter-search">
          <input
            type="search"
            inputMode="search"
            placeholder="Search your Boogas…"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search Boogas by name"
          />
          {query && (
            <button
              type="button"
              className="filter-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="view-toggle" role="group" aria-label="Roster view size">
        <button
          className={view === "large" ? "active" : ""}
          onClick={() => changeView("large")}
          type="button"
        >
          ▭ Large
        </button>
        <button
          className={view === "small" ? "active" : ""}
          onClick={() => changeView("small")}
          type="button"
        >
          ▦ Small
        </button>
      </div>

      <BottomSheet open={sheet === "filter"} title="Filter Boogas" onClose={() => setSheet(null)}>
        {presentArchetypes.length > 1 && (
          <div className="sheet-section">
            <span className="sheet-label">Class</span>
            <div className="filter-chips" role="group" aria-label="Filter by class">
              {presentArchetypes.map((a) => {
                const on = archetypes.has(a);
                return (
                  <button
                    key={a}
                    type="button"
                    className={on ? "chip active" : "chip"}
                    style={on ? { borderColor: archetypeColor(a), color: archetypeColor(a) } : undefined}
                    onClick={() => toggleArchetype(a)}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {presentTiers.length > 1 && (
          <div className="sheet-section">
            <span className="sheet-label">Rarity</span>
            <div className="filter-chips" role="group" aria-label="Filter by rarity">
              {presentTiers.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={tiers.has(t) ? "chip active" : "chip"}
                  onClick={() => toggleTier(t)}
                >
                  {TIER_LABEL[t]}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="sheet-foot">
          <Button variant="ghost" onClick={clearFilters}>
            Clear all
          </Button>
          <Button variant="lava" onClick={() => setSheet(null)}>
            Done
          </Button>
        </div>
      </BottomSheet>

      <BottomSheet open={sheet === "sort"} title="Sort Boogas" onClose={() => setSheet(null)}>
        <div className="sheet-section sort-list" role="group" aria-label="Sort Boogas">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={sort === o.value ? "sort-row active" : "sort-row"}
              onClick={() => {
                changeSort(o.value);
                setSheet(null);
              }}
            >
              {o.label}
              {sort === o.value && <span aria-hidden>✓</span>}
            </button>
          ))}
        </div>
      </BottomSheet>

      {shown.length === 0 ? (
        <div className="roster-empty">
          <p className="hint">No Boogas match — loosen the filters.</p>
          <Button variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <div className={`roster-grid ${view}`}>
          {shown.map((b) => {
            const idx = selected.indexOf(b.identifier);
            return (
              <BoogaCard
                key={b.identifier}
                booga={b}
                selected={idx >= 0}
                badge={idx >= 0 ? String(idx + 1) : undefined}
                onClick={() => toggle(b.identifier)}
              />
            );
          })}
        </div>
      )}

      <div className="action-bar sticky">
        {hiddenSelected > 0 && (
          <p className="hint center filter-hidden">
            {hiddenSelected} selected{" "}
            {hiddenSelected === 1 ? "Booga is" : "Boogas are"} hidden by filters
          </p>
        )}
        {canOne && (
          <Button variant="lava" full onClick={() => onOneVsOne(selected[0]!, selected[1]!)} disabled={busy}>
            ⚔️ 1v1 Brawl
          </Button>
        )}
        {canPool && (
          <Button full variant="cyber" onClick={() => onPool(selected[0]!)} disabled={busy}>
            🎲 Pool Brawl
          </Button>
        )}
        {selected.length === 0 && modes.tournament && (
          // Trials = whole squad: always the full roster, ignore active filters.
          <Button full onClick={() => onTournament(roster.map((b) => b.identifier))} disabled={busy}>
            🏟️ Booga Trials
          </Button>
        )}
        {selected.length === 0 && !modes.tournament && (
          <p className="hint center">Select a Booga to brawl.</p>
        )}
        {/* PvP: the lobby is always open; challenge/queue need exactly 1 pick. */}
        <Button
          variant="ghost"
          full
          disabled={busy}
          onClick={() => {
            haptic("light");
            setPvpSheet(true);
          }}
        >
          🥊 PvP Brawl
        </Button>
      </div>

      <BottomSheet open={pvpSheet} title="PvP Brawl" onClose={() => setPvpSheet(false)}>
        <div className="sheet-section">
          <p className="hint">
            {canPool
              ? `Send ${roster.find((b) => b.identifier === selected[0])?.name ?? "your Booga"} against a real holder's Booga. Ooga versus Booga.`
              : "Pick exactly 1 Booga to challenge or queue — or wander into the lobby."}
          </p>
          <Button
            variant="lava"
            full
            disabled={busy || !canPool}
            onClick={() => {
              setPvpSheet(false);
              onChallenge(selected[0]!);
            }}
          >
            🔗 Challenge a Fren
          </Button>
          <Button
            variant="cyber"
            full
            disabled={busy || !canPool}
            onClick={() => {
              setPvpSheet(false);
              onQueue(selected[0]!);
            }}
          >
            🎯 Find an Opponent
          </Button>
          <Button
            full
            disabled={busy}
            onClick={() => {
              setPvpSheet(false);
              onLobby();
            }}
          >
            🏕️ Lobby Cave
          </Button>
        </div>
      </BottomSheet>
    </div>
  );
}

// --- PvP: challenge share ----------------------------------------------------

function challengeShareText(booga: Booga): string {
  return `⚔️ My ${booga.name} (${booga.archetype}) throws down the gauntlet in Booga Brawl! First to accept gets clubbed. Ooga Booga!`;
}

export function ChallengeShareScreen({
  data,
  busy,
  onCancel,
  onBack,
}: {
  data: ChallengeCreated;
  busy: boolean;
  onCancel: () => void;
  onBack: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const link = data.link;

  function copyLink() {
    if (!link) return;
    haptic("light");
    try {
      void navigator.clipboard?.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — share buttons remain the primary path */
    }
  }

  const directed = data.targetTgId !== undefined;

  return (
    <div className="screen">
      <TopBar title="⚔️ Challenge Sent" onBack={onBack} />
      <div className="card">
        <BoogaCard booga={data.booga} />
        <p className="hint center">
          {directed
            ? `${data.booga.name} stands ready — your fren got a Telegram DM with the challenge. Now you wait. Ominously.`
            : `${data.booga.name} awaits a challenger. Share the link — the first fren (not you!) to accept gets brawled. Link lasts 24 hours.`}
        </p>
        {directed ? null : link ? (
          <>
            <ShareBar text={challengeShareText(data.booga)} link={link} />
            <Button variant="ghost" full onClick={copyLink}>
              {copied ? "Copied! 🦴" : "Copy link 📋"}
            </Button>
          </>
        ) : (
          <p className="hint center">
            Challenge code: <strong>{data.code}</strong>
          </p>
        )}
        <Button variant="ghost" full disabled={busy} onClick={onCancel}>
          Cancel challenge ✕
        </Button>
      </div>
    </div>
  );
}

// --- PvP: challenge accept ----------------------------------------------------

const CHALLENGE_DEAD_MSG: Record<string, string> = {
  expired: "This challenge went stale — Boogas can't wait forever.",
  cancelled: "The challenger got cold feet and scratched it off the wall.",
  accepted: "Too slow — another Booga already answered this one.",
};

export function ChallengeAcceptScreen({
  preview,
  authed,
  roster,
  busy,
  onConnect,
  onAccept,
  onWatchReplay,
  onExit,
}: {
  preview: ChallengePreview | null;
  authed: boolean;
  roster: Booga[];
  busy: boolean;
  onConnect: () => void;
  onAccept: (boogaId: string) => void;
  onWatchReplay: (fightId: number) => void;
  onExit: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);

  if (!preview) {
    return (
      <div className="screen">
        <TopBar title="⚔️ PvP Challenge" onBack={onExit} />
        <Spinner label="Reading the cave wall…" />
      </div>
    );
  }

  const dead = preview.status !== "pending";
  // The same physical NFT can't fight itself (you might hold it too).
  const pickable = roster.filter(
    (b) => b.identifier !== preview.challengerBooga.identifier,
  );

  return (
    <div className="screen">
      <TopBar title="⚔️ PvP Challenge" onBack={onExit} />
      <div className="card">
        <p className="hint center">A wild challenger appears:</p>
        <BoogaCard booga={preview.challengerBooga} />
      </div>

      {dead ? (
        <div className="card">
          <p className="hint center">
            {CHALLENGE_DEAD_MSG[preview.status] ?? "This challenge is over."}
          </p>
          {preview.status === "accepted" && preview.fightId !== undefined && (
            <Button variant="cyber" full disabled={busy} onClick={() => onWatchReplay(preview.fightId!)}>
              Watch the brawl 🍖
            </Button>
          )}
          <Button variant="ghost" full onClick={onExit}>
            Back
          </Button>
        </div>
      ) : !authed ? (
        <div className="card">
          <p className="hint center">
            Connect your wallet and pick a Booga to answer the challenge.
          </p>
          <Button variant="lava" full disabled={busy} onClick={onConnect}>
            {busy ? "Summoning Boogas…" : "Connect Wallet 🦴"}
          </Button>
        </div>
      ) : (
        <>
          <p className="hint center">Pick your fighter:</p>
          {pickable.length === 0 ? (
            <p className="hint center">
              No eligible Boogas in your cave for this one, fam.
            </p>
          ) : (
            <div className="roster-grid small">
              {pickable.map((b) => (
                <BoogaCard
                  key={b.identifier}
                  booga={b}
                  selected={picked === b.identifier}
                  onClick={() => {
                    haptic("light");
                    setPicked(b.identifier);
                  }}
                />
              ))}
            </div>
          )}
          <div className="action-bar sticky">
            <Button
              variant="lava"
              full
              disabled={!picked || busy}
              onClick={() => picked && onAccept(picked)}
            >
              {busy ? "Brawling…" : "Accept & Brawl ⚔️"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// --- PvP: lobby cave -----------------------------------------------------------

/** Hours left before a wall challenge goes stale. */
function hoursLeft(expiresAt: number): string {
  const h = Math.max(0, Math.round((expiresAt - Date.now()) / 3_600_000));
  return h >= 1 ? `${h}h left` : "almost gone";
}

export function LobbyScreen({
  roster,
  busy,
  onAcceptChallenge,
  onChallengePlayer,
  onSessionExpired,
  onExit,
}: {
  roster: Booga[];
  busy: boolean;
  onAcceptChallenge: (code: string) => void;
  onChallengePlayer: (tgId: number, boogaId: string) => void;
  onSessionExpired: () => void;
  onExit: () => void;
}) {
  const [wall, setWall] = useState<LobbyChallenge[] | null>(null);
  const [dir, setDir] = useState<DirectoryStatus | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DirectoryEntry[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  // Which player we're picking a Booga for (directed challenge).
  const [challenging, setChallenging] = useState<DirectoryEntry | null>(null);
  const [error, setError] = useState<string | undefined>();

  function fail(e: unknown) {
    if (e instanceof ApiError && e.status === 401) {
      onSessionExpired();
      return;
    }
    setError(e instanceof Error ? e.message : "Something grunked. Try again.");
  }

  // The wall + own directory status, on entry.
  useEffect(() => {
    api.lobbyChallenges().then(setWall).catch(fail);
    api.directoryMe().then(setDir).catch(fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced player search (min 2 chars, matching the server rule).
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .searchPlayers(q)
        .then((r) => {
          setResults(r);
          setSearching(false);
        })
        .catch((e) => {
          setSearching(false);
          fail(e);
        });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  function toggleListing() {
    haptic("light");
    const work = dir?.listed ? api.directoryOptOut().then(() => api.directoryMe()) : api.directoryOptIn();
    work.then(setDir).catch(fail);
  }

  const trimmedCode = codeInput.trim().replace(/^.*startapp=c_|^c_/, "");

  return (
    <div className="screen">
      <TopBar title="🏕️ Lobby Cave" onBack={onExit} />
      {error ? (
        <div className="toast" onClick={() => setError(undefined)}>
          {error}
        </div>
      ) : null}

      {/* Find a caveman ------------------------------------------------- */}
      <div className="card">
        <span className="sheet-label">Find a caveman</span>
        <div className="filter-search">
          <input
            type="search"
            inputMode="search"
            placeholder="Search by username or herotag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search players"
          />
          {query && (
            <button
              type="button"
              className="filter-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searching ? (
          <Spinner label="Sniffing around…" />
        ) : results !== null ? (
          results.length === 0 ? (
            <p className="hint center">No caveman by that name in the lobby.</p>
          ) : (
            <ol className="leaderboard">
              {results.map((p) => (
                <li key={p.telegramUserId}>
                  <div className="lb-row">
                    <span className="lb-name">
                      {p.username ? `@${p.username}` : `Holder #${p.telegramUserId}`}
                      {p.herotag ? <small>🏷 {p.herotag}</small> : null}
                    </span>
                    <span className="lb-wl">
                      {p.wins}W–{p.losses}L
                    </span>
                    <Button
                      variant="lava"
                      disabled={busy}
                      onClick={() => {
                        haptic("light");
                        setChallenging(p);
                      }}
                    >
                      Challenge ⚔️
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
          )
        ) : (
          <p className="hint center">
            Only cavemen who listed themselves are findable.{" "}
            {dir?.listed
              ? "You're on the wall — frens can find you."
              : "You're invisible right now."}
          </p>
        )}
        <Button variant="ghost" full onClick={toggleListing}>
          {dir?.listed ? "Remove me from the lobby 🙈" : "List me in the lobby 📢"}
        </Button>
        {dir?.listed && dir.entry?.herotag ? (
          <p className="footnote">Listed as @{dir.entry.username ?? "?"} · 🏷 {dir.entry.herotag}</p>
        ) : null}
      </div>

      {/* Enter a code ---------------------------------------------------- */}
      <div className="card">
        <span className="sheet-label">Got a challenge code?</span>
        <div className="filter-search">
          <input
            type="text"
            placeholder="Paste code or link…"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            aria-label="Challenge code"
          />
        </div>
        <Button
          variant="cyber"
          full
          disabled={!trimmedCode || busy}
          onClick={() => onAcceptChallenge(trimmedCode)}
        >
          Open challenge 🔎
        </Button>
      </div>

      {/* The wall of open challenges -------------------------------------- */}
      <div className="card">
        <span className="sheet-label">The challenge wall</span>
        {wall === null ? (
          <Spinner label="Reading the scratchings…" />
        ) : wall.length === 0 ? (
          <p className="hint center">
            Wall is bare. Scratch the first challenge from your roster!
          </p>
        ) : (
          <div className="roster-grid small">
            {wall.map((c) => (
              <div key={c.code}>
                <BoogaCard booga={c.booga} onClick={() => onAcceptChallenge(c.code)} />
                <p className="hint center">
                  {hoursLeft(c.expiresAt)} ·{" "}
                  <button className="link" onClick={() => onAcceptChallenge(c.code)}>
                    Accept ⚔️
                  </button>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Booga picker for a directed challenge ----------------------------- */}
      <BottomSheet
        open={challenging !== null}
        title={`Challenge ${challenging?.username ? `@${challenging.username}` : "caveman"}`}
        onClose={() => setChallenging(null)}
      >
        <div className="sheet-section">
          <p className="hint">Pick which Booga carries your honor:</p>
          <div className="roster-grid small">
            {roster.map((b) => (
              <BoogaCard
                key={b.identifier}
                booga={b}
                onClick={() => {
                  const target = challenging;
                  setChallenging(null);
                  if (target) onChallengePlayer(target.telegramUserId, b.identifier);
                }}
              />
            ))}
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

// --- PvP: matchmaking queue ----------------------------------------------------

type QueuePhase = "joining" | "waiting" | "expired" | "error";

export function QueueScreen({
  booga,
  onMatched,
  onSessionExpired,
  onExit,
}: {
  booga: Booga;
  onMatched: (data: PvpFightResponse) => void;
  onSessionExpired: () => void;
  onExit: () => void;
}) {
  const [phase, setPhase] = useState<QueuePhase>("joining");
  const [message, setMessage] = useState<string | undefined>();
  // Poll cadence comes from the server; refs keep the effect's cleanup honest.
  const pollMs = useRef(2500);
  const matchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function fail(e: unknown) {
      if (cancelled) return;
      if (e instanceof ApiError && e.status === 401) {
        onSessionExpired();
        return;
      }
      setMessage(e instanceof Error ? e.message : "Something grunked. Try again.");
      setPhase("error");
    }

    function handle(res: QueueJoinResult | QueueStatusResult) {
      if (cancelled) return;
      if (res.status === "matched") {
        matchedRef.current = true;
        haptic("heavy");
        onMatched(res);
      } else if (res.status === "waiting") {
        pollMs.current = res.pollMs;
        setPhase("waiting");
        timer = setTimeout(poll, pollMs.current);
      } else {
        // expired / idle — the entry lapsed before a match.
        setPhase("expired");
      }
    }

    function poll() {
      api.queueStatus().then(handle).catch(fail);
    }

    api.queueJoin(booga.identifier, Math.floor(Math.random() * 2 ** 31)).then(handle).catch(fail);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      // Leaving the screen un-matched = leaving the queue. Best-effort.
      if (!matchedRef.current) void api.queueLeave().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booga.identifier]);

  return (
    <div className="screen">
      <TopBar title="🎯 Matchmaking" onBack={onExit} />
      <div className="card">
        <BoogaCard booga={booga} />
        {phase === "joining" || phase === "waiting" ? (
          <>
            <Spinner label="The matchmaking shaman is sniffing out an opponent…" />
            <p className="hint center">
              Rarity-banded first; after a while, anyone brave enough will do.
            </p>
            <Button variant="ghost" full onClick={onExit}>
              Leave queue ✕
            </Button>
          </>
        ) : phase === "expired" ? (
          <>
            <p className="hint center">
              No brawler showed up. The cave is quiet… too quiet.
            </p>
            <Button variant="ghost" full onClick={onExit}>
              Back to roster
            </Button>
          </>
        ) : (
          <>
            <p className="hint center">{message}</p>
            <Button variant="ghost" full onClick={onExit}>
              Back to roster
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Fight (animated round-by-round) ---------------------------------------

function hpAfter(lines: RenderedLine[], a: FighterSummary, b: FighterSummary) {
  let hpA = a.maxHp;
  let hpB = b.maxHp;
  for (const l of lines) {
    const e = l.event;
    if (!e) continue;
    if (e.action === "heal") {
      if (e.actor === a.identifier) hpA = e.remainingHP;
      else if (e.actor === b.identifier) hpB = e.remainingHP;
    } else {
      if (e.target === a.identifier) hpA = e.remainingHP;
      else if (e.target === b.identifier) hpB = e.remainingHP;
    }
  }
  return { hpA, hpB };
}

function lineClass(l: RenderedLine): string {
  const s = l.event?.special;
  if (s === "crit") return "line-crit";
  if (s === "miss") return "line-miss";
  if (s === "ko" || s === "stalemate") return "line-ko";
  if (s === "zombie_save") return "line-zombie";
  if (l.event?.action === "heal") return "line-heal";
  if (l.round === 0) return "line-intro";
  return "";
}

// Delay between auto-played log lines (ms). Matches the old auto-advance tempo.
const AUTO_PLAY_MS = 900;
const AUTO_PLAY_KEY = "bb_autoplay";

/** Read the persisted Auto-play preference (defaults off). */
function getAutoPlay(): boolean {
  try {
    return localStorage.getItem(AUTO_PLAY_KEY) === "1";
  } catch {
    return false;
  }
}

function setAutoPlay(on: boolean): void {
  try {
    localStorage.setItem(AUTO_PLAY_KEY, on ? "1" : "0");
  } catch {
    /* storage unavailable (e.g. private mode) — preference just won't persist */
  }
}

// Roster density: "large" = one hero card per row, "small" = compact ~2-up grid.
type RosterView = "large" | "small";
const ROSTER_VIEW_KEY = "bb_rosterview";

/** Read the persisted roster view preference (defaults to large). */
function getRosterView(): RosterView {
  try {
    return localStorage.getItem(ROSTER_VIEW_KEY) === "small" ? "small" : "large";
  } catch {
    return "large";
  }
}

function setRosterView(view: RosterView): void {
  try {
    localStorage.setItem(ROSTER_VIEW_KEY, view);
  } catch {
    /* storage unavailable — preference just won't persist */
  }
}

// --- Roster filtering & sorting --------------------------------------------

type RosterSort = "rank-asc" | "rank-desc" | "name" | "hp" | "atk" | "def" | "spd" | "crit";

const SORT_OPTIONS: { value: RosterSort; label: string }[] = [
  { value: "rank-asc", label: "Rarest" },
  { value: "rank-desc", label: "Common" },
  { value: "name", label: "Name" },
  { value: "hp", label: "HP" },
  { value: "atk", label: "ATK" },
  { value: "def", label: "DEF" },
  { value: "spd", label: "SPD" },
  { value: "crit", label: "CRIT" },
];

const TIER_ORDER: RarityTier[] = ["legendary", "epic", "rare", "common"];
const TIER_LABEL: Record<RarityTier, string> = {
  legendary: "Legendary",
  epic: "Epic",
  rare: "Rare",
  common: "Common",
};

const ROSTER_SORT_KEY = "bb_rostersort";

/** Read the persisted roster sort preference (defaults to rarest-first). */
function getRosterSort(): RosterSort {
  try {
    const v = localStorage.getItem(ROSTER_SORT_KEY) as RosterSort | null;
    return v && SORT_OPTIONS.some((o) => o.value === v) ? v : "rank-asc";
  } catch {
    return "rank-asc";
  }
}

function setRosterSort(sort: RosterSort): void {
  try {
    localStorage.setItem(ROSTER_SORT_KEY, sort);
  } catch {
    /* storage unavailable — preference just won't persist */
  }
}

/** Compare two Boogas by the chosen sort. Stats sort high→low; rank-asc = rarest first. */
function compareBoogas(a: Booga, b: Booga, sort: RosterSort): number {
  switch (sort) {
    case "rank-asc":
      return a.rank - b.rank;
    case "rank-desc":
      return b.rank - a.rank;
    case "name":
      return a.name.localeCompare(b.name);
    default:
      // hp/atk/def/spd/crit — higher stat first, tie-break by rank
      return b.statBlock[sort] - a.statBlock[sort] || a.rank - b.rank;
  }
}

function FighterPanel({ f, hp }: { f: FighterSummary; hp: number }) {
  return (
    <div className="fighter">
      <BoogaArt src={f.mediaUrl} alt={f.name} size={105} />
      <HpBar name={f.name} hp={hp} max={f.maxHp} archetype={f.archetype} />
    </div>
  );
}

export function FightScreen({
  data,
  title,
  onRematch,
  onExit,
}: {
  data: FightResponse;
  title?: string;
  onRematch?: () => void;
  onExit: () => void;
}) {
  const { fight, lines } = data;
  const { a, b } = fight.fighters;
  const [shown, setShown] = useState(1);
  const [auto, setAuto] = useState(getAutoPlay);
  const logRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLParagraphElement>(null);

  const visible = lines.slice(0, shown);
  const { hpA, hpB } = hpAfter(visible, a, b);
  const done = shown >= lines.length;
  const winner = fight.winner === a.identifier ? a : b;
  const winnerName = winner.name;

  // Default tempo is user-paced: each tap (Next, or anywhere on the arena/log)
  // surfaces the next line, so the log never floods as a wall of text. Flip the
  // Auto-play switch and the lines reveal themselves on a timer instead.
  const advance = () => {
    if (done) return;
    setShown((s) => Math.min(s + 1, lines.length));
    haptic("light");
  };

  // Auto-play: while on (and not finished), tick a line out every AUTO_PLAY_MS.
  useEffect(() => {
    if (!auto || done) return;
    const t = setTimeout(() => {
      setShown((s) => Math.min(s + 1, lines.length));
      haptic("light");
    }, AUTO_PLAY_MS);
    return () => clearTimeout(t);
  }, [auto, done, shown, lines.length]);

  // Remember the player's Auto-play preference across fights.
  function toggleAuto(on: boolean) {
    setAuto(on);
    setAutoPlay(on);
  }

  // Keep the freshly-revealed line centered in the log viewport, so the newest beat
  // is the focal point with spent lines trailing above it. Scoped to the log's own
  // scroll box (computed deltas, not scrollIntoView) so the page never jumps.
  useEffect(() => {
    const box = logRef.current;
    const cur = currentRef.current;
    if (!box || !cur) return;
    const boxRect = box.getBoundingClientRect();
    const curRect = cur.getBoundingClientRect();
    const delta = curRect.top - boxRect.top - (box.clientHeight - cur.clientHeight) / 2;
    box.scrollTo({ top: box.scrollTop + delta, behavior: "smooth" });
  }, [shown]);

  return (
    <div className="screen fight">
      {title ? <Plaque>{title}</Plaque> : null}
      <div className="arena" onClick={advance}>
        <FighterPanel f={a} hp={hpA} />
        <span className="vs">VS</span>
        <FighterPanel f={b} hp={hpB} />
      </div>

      <div
        className={`log${!done ? " advance" : ""}`}
        ref={logRef}
        onClick={advance}
      >
        {visible.map((l, i) => {
          const isCurrent = i === visible.length - 1;
          return (
            <p
              key={i}
              ref={isCurrent ? currentRef : undefined}
              className={`log-line ${lineClass(l)}${isCurrent ? " current" : ""}`}
            >
              {l.text}
            </p>
          );
        })}
      </div>

      <div className="action-bar sticky">
        {!done && (
          <>
            <label className="switch">
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => toggleAuto(e.target.checked)}
              />
              <span className="switch-track">
                <span className="switch-thumb" />
              </span>
              <span className="switch-label">Auto-play</span>
            </label>
            {!auto && (
              <Button variant="lava" full onClick={advance}>
                Next ▶
              </Button>
            )}
            <Button variant="ghost" full onClick={() => setShown(lines.length)}>
              Skip ⏭
            </Button>
          </>
        )}
        {done && (
          <>
            <WinnerPfp fighter={winner} />
            <div className="winner-banner">
              <span className="winner-title">
                <img className="winner-logo" src="/brand/B_Rank_Gold.png" alt="" aria-hidden />
                {winnerName} wins!
              </span>
              <span className="winner-sub">
                ({fight.endReason}, {fight.rounds} rounds)
              </span>
            </div>
            <ShareBar
              text={fightShareText(winner, fight.rounds, fight.endReason === "stalemate")}
            />
            {onRematch ? (
              <Button full onClick={onRematch}>
                Rematch 🔁
              </Button>
            ) : null}
            <Button variant="ghost" full onClick={onExit}>
              Back to roster
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Tournament -------------------------------------------------------------

export function TournamentScreen({
  data,
  nameOf,
  onExit,
}: {
  data: TournamentResponse;
  nameOf: (id: string | null) => string;
  onExit: () => void;
}) {
  const [watchFinal, setWatchFinal] = useState(false);
  const finalFight = data.fights[data.fights.length - 1];
  const champName = nameOf(data.champion);

  if (watchFinal && finalFight) {
    return (
      <FightScreen
        data={finalFight}
        title="🏟️ The Final"
        onExit={() => setWatchFinal(false)}
      />
    );
  }

  return (
    <div className="screen">
      <Plaque>🏟️ Booga Trials</Plaque>
      <div className="champion">
        🏆 Champion: <strong>{champName}</strong>
      </div>

      <div className="bracket">
        {data.bracket.map((round, ri) => (
          <div key={ri} className="bracket-round">
            <h3>{ri === data.bracket.length - 1 ? "Final" : `Round ${ri + 1}`}</h3>
            {round.map((m, mi) => (
              <div key={mi} className="bracket-match">
                <span className={m.winner === m.a ? "win" : "lose"}>{nameOf(m.a)}</span>
                <span className="vs-mini">vs</span>
                <span className={m.winner === m.b ? "win" : "lose"}>
                  {m.b ? nameOf(m.b) : "— bye —"}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="action-bar sticky">
        {finalFight ? (
          <Button full onClick={() => setWatchFinal(true)}>
            ▶ Watch the final
          </Button>
        ) : null}
        <ShareBar text={tournamentShareText(champName, data.fights.length)} />
        <Button variant="ghost" full onClick={onExit}>
          Back to roster
        </Button>
      </div>
    </div>
  );
}

// --- Leaderboard ------------------------------------------------------------

/** One row on the per-player PvP board. Username when known, else a themed id. */
function PvpRow({ e, pos }: { e: PvpLeaderboardEntry; pos: number }) {
  return (
    <li>
      <div className="lb-row">
        <span className="lb-pos">{pos}</span>
        <span className="lb-name">
          {e.username ? `@${e.username}` : `Booga Holder #${e.telegramUserId}`}
        </span>
        <span className="lb-wl">
          {e.wins}W–{e.losses}L<small>{Math.round(e.winRate * 100)}%</small>
        </span>
      </div>
    </li>
  );
}

export function LeaderboardScreen({
  entries,
  pvpEntries,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  tab,
  onTab,
  onOpenDetail,
  detailOpen,
  detail,
  detailLoading,
  onCloseDetail,
  onBack,
}: {
  entries: LeaderboardEntry[];
  pvpEntries: PvpLeaderboardEntry[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  tab: BoardTab;
  onTab: (tab: BoardTab) => void;
  onOpenDetail: (id: string) => void;
  detailOpen: boolean;
  detail: BoogaDetail | null;
  detailLoading: boolean;
  onCloseDetail: () => void;
  onBack: () => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);

  // Auto-load the next page when the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((rows) => {
      if (rows.some((r) => r.isIntersecting)) onLoadMore();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, onLoadMore]);

  const setTab = (t: BoardTab) => {
    if (t === tab) return;
    haptic("light");
    onTab(t);
  };

  return (
    <div className="screen">
      <TopBar
        title={
          <>
            <img className="plaque-trophy" src="/brand/Rank.png" alt="" aria-hidden />
            Leaderboard
          </>
        }
        onBack={onBack}
      />
      <div className="view-toggle lb-toggle">
        <button
          type="button"
          className={tab === "daily" ? "active" : ""}
          onClick={() => setTab("daily")}
        >
          Today
        </button>
        <button
          type="button"
          className={tab === "all" ? "active" : ""}
          onClick={() => setTab("all")}
        >
          All-time
        </button>
        <button
          type="button"
          className={tab === "pvp" ? "active" : ""}
          onClick={() => setTab("pvp")}
        >
          PvP
        </button>
      </div>
      {loading ? (
        <Spinner label="Counting wins…" />
      ) : tab === "pvp" ? (
        pvpEntries.length === 0 ? (
          <p className="hint center">
            No PvP brawls yet. Challenge a fren and make history!
          </p>
        ) : (
          <ol className="leaderboard">
            {pvpEntries.map((e, i) => (
              <PvpRow key={e.telegramUserId} e={e} pos={i + 1} />
            ))}
          </ol>
        )
      ) : entries.length === 0 ? (
        <p className="hint center">
          {tab === "daily"
            ? "No brawls today yet. Be the first to put a bounce in your step!"
            : "No brawls yet. Be the first to put a bounce in your step!"}
        </p>
      ) : (
        <>
          <ol className="leaderboard">
            {entries.map((e, i) => (
              <li key={e.identifier}>
                <button
                  type="button"
                  className="lb-row"
                  onClick={() => {
                    haptic("light");
                    onOpenDetail(e.identifier);
                  }}
                >
                  <span className="lb-pos">{i + 1}</span>
                  <BoogaArt src={e.mediaUrl} alt={e.name} size={56} />
                  <span className="lb-name">
                    {e.name}
                    <small>
                      <ArchetypePill archetype={e.archetype} /> <RarityBadge rank={e.rank} />
                    </small>
                  </span>
                  <span className="lb-wl">
                    {e.wins}W–{e.losses}L<small>{Math.round(e.winRate * 100)}%</small>
                  </span>
                </button>
              </li>
            ))}
          </ol>
          {hasMore ? (
            <div ref={sentinel}>
              {loadingMore ? (
                <Spinner label="Loading more brawlers…" />
              ) : (
                <Button variant="ghost" full onClick={onLoadMore}>
                  Load more
                </Button>
              )}
            </div>
          ) : (
            <p className="hint center">That's every Booga who's brawled. Ooga Booga!</p>
          )}
        </>
      )}
      <BottomSheet
        open={detailOpen}
        title={detail?.booga.name ?? "Booga"}
        onClose={onCloseDetail}
      >
        {detailLoading || !detail ? (
          <Spinner label="Digging up the bones…" />
        ) : (
          <BoogaDetailSheet detail={detail} />
        )}
      </BottomSheet>
    </div>
  );
}

/** Shorten an erd1 address for display: erd1abcd…wxyz. Never stored. */
function shortAddress(addr: string): string {
  return addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr;
}

function BoogaDetailSheet({ detail }: { detail: BoogaDetail }) {
  const { booga, record, holder } = detail;
  const total = record.wins + record.losses;
  const winRate = total > 0 ? Math.round((record.wins / total) * 100) : 0;
  const traits = Object.entries(booga.traits);
  return (
    <div className="booga-detail">
      <div className="detail-head">
        <BoogaArt
          src={pfpUrl(booga.identifier, booga.name) ?? booga.mediaUrl}
          alt={booga.name}
          size={96}
        />
        <div className="detail-id">
          <ArchetypePill archetype={booga.archetype} /> <RarityBadge rank={booga.rank} />
          <p className="detail-record">
            {record.wins}W–{record.losses}L <small>{winRate}% win rate</small>
          </p>
        </div>
      </div>

      <StatBars stats={booga.statBlock} />

      <p className="detail-holder">
        <span className="detail-label">Held by</span>{" "}
        {holder ? (
          holder.herotag ? (
            <strong>@{holder.herotag}</strong>
          ) : (
            <strong>{shortAddress(holder.address)}</strong>
          )
        ) : (
          <em>unknown — the chain is busy grunting</em>
        )}
      </p>

      {traits.length > 0 ? (
        <div className="detail-traits">
          <span className="detail-label">Traits</span>
          <ul>
            {traits.map(([cat, t]) => (
              <li key={cat}>
                <span className="trait-cat">{cat}</span>
                <span className="trait-val">{t.value}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
